import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import LiveCaptureModal from './LiveCaptureModal';
import PhotoTaskCard from './PhotoTaskCard';
import { SELFIE_TASKS, POSE_A_TASKS, POSE_B_TASKS } from '../config/photoTasks';
import './PhotoUpload.css';

// Mobile-Friendly Swipeable Example Carousel with Dot Indicators
// eslint-disable-next-line no-unused-vars
const ExampleCarousel = ({ photos, categoryLabel, onSelectImage }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  if (!photos || photos.length === 0) {
    return (
      <div className="example-empty-box">
        <span>No example images available for {categoryLabel}.</span>
      </div>
    );
  }

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const idx = Math.round(scrollLeft / Math.max(1, clientWidth * 0.75));
      setActiveIndex(Math.min(photos.length - 1, Math.max(0, idx)));
    }
  };

  const scrollToSlide = (idx) => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.clientWidth * 0.75;
      scrollRef.current.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
      setActiveIndex(idx);
    }
  };

  return (
    <div className="example-carousel-wrapper">
      <div className="example-carousel-track" ref={scrollRef} onScroll={handleScroll}>
        {photos.map((photo, i) => {
          const imgUrl = `${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${photo.id}/file`;
          return (
            <div key={photo.id || i} className="example-carousel-card" onClick={() => onSelectImage(imgUrl)}>
              <img
                src={imgUrl}
                alt={`Example for ${photo.category || categoryLabel}`}
                className="example-carousel-img"
              />
              <div className="example-carousel-tag">
                {(photo.category || categoryLabel).replace('present_', '')}
              </div>
            </div>
          );
        })}
      </div>
      {photos.length > 1 && (
        <div className="example-carousel-dots">
          {photos.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`carousel-dot ${i === activeIndex ? 'active' : ''}`}
              onClick={() => scrollToSlide(i)}
              aria-label={`View example ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PhotoUpload = () => {
  const { userName } = useParams();
  const navigate = useNavigate();

  const storedUserId = sessionStorage.getItem('userId');
  const [actualUserId, setActualUserId] = useState(storedUserId || null);
  // eslint-disable-next-line no-unused-vars
  const [userGender, setUserGender] = useState('');
  // Sequence: Step 1 (Historical) -> Step 2 (Selfies) -> Step 3 (Poses) -> Step 4 (Review/Hand In)
  const [currentStep, setCurrentStep] = useState('old'); 
  const [presentPhotos, setPresentPhotos] = useState([]);
  const [postAPhotos, setPostAPhotos] = useState([]);
  const [postBPhotos, setPostBPhotos] = useState([]);
  const [oldPhotos, setOldPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [examplePhotos, setExamplePhotos] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [displayName, setDisplayName] = useState(userName);
  const [duplicateDateWarning, setDuplicateDateWarning] = useState('');
  const [showWhyModal, setShowWhyModal] = useState(false);

  // Guided Step-by-Step Task States & Cooldown
  const [activeSelfieIndex, setActiveSelfieIndex] = useState(0);
  const [activePoseAIndex, setActivePoseAIndex] = useState(0);
  const [activePoseBIndex, setActivePoseBIndex] = useState(0);
  const [selfieCooldownSeconds, setSelfieCooldownSeconds] = useState(0);
  const [activeTaskForCamera, setActiveTaskForCamera] = useState(null);
  const [showAllPresentPhotos, setShowAllPresentPhotos] = useState(false);
  const [showAllPostAPhotos, setShowAllPostAPhotos] = useState(false);
  const [showAllPostBPhotos, setShowAllPostBPhotos] = useState(false);

  // Cooldown countdown timer for candidate selfie pacing
  useEffect(() => {
    if (selfieCooldownSeconds <= 0) return;
    const interval = setInterval(() => {
      setSelfieCooldownSeconds(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [selfieCooldownSeconds]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e, uploadHandler) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fakeEvent = { target: { files: e.dataTransfer.files } };
      uploadHandler(fakeEvent);
    }
  };

  // Fetch existing user photos on load
  const loadUserPhotos = useCallback(async () => {
    if (!userName || userName === 'undefined') {
      navigate('/login');
      return;
    }
    
    // Auth Check
    const sessionUserName = sessionStorage.getItem('userName');
    const isAdminToken = sessionStorage.getItem('adminToken');
    if (!isAdminToken && sessionUserName && sessionUserName !== userName) {
      navigate(`/upload/${encodeURIComponent(sessionUserName)}`);
      return;
    }

    try {
      setLoading(true);
      const data = await apiService.getUserStatus(userName);
      if (data) {
        if (data.user?.id) {
          setActualUserId(data.user.id);
        }
        if (data.user?.name) setDisplayName(data.user.name);
        if (data.user?.gender) setUserGender(data.user.gender);
        if (data.old_photo_status?.photos) {
          setOldPhotos(data.old_photo_status.photos);
        }
        if (data.present_photo_status?.photos) {
          setPresentPhotos(data.present_photo_status.photos);
        }
        if (data.post_a_photo_status?.photos) {
          setPostAPhotos(data.post_a_photo_status.photos);
        }
        if (data.post_b_photo_status?.photos) {
          setPostBPhotos(data.post_b_photo_status.photos);
        }
      }
    } catch (err) {
      console.error('Error loading photos:', err);
      setError('Failed to load existing photos.');
    } finally {
      setLoading(false);
    }
  }, [userName, navigate]);

  useEffect(() => {
    loadUserPhotos();
    apiService.getExamplePhotos().then(data => {
      setExamplePhotos(data.photos || []);
    }).catch(err => console.error('Failed to fetch example photos:', err));
  }, [loadUserPhotos]);

  const validateFileSize = (file, isOld = true) => {
    if (isOld && file.size < 128 * 1024) {
      setError(`File "${file.name}" is too small (${(file.size / 1024).toFixed(1)}KB). Minimum size is 128KB.`);
      return false;
    }
    if (!isOld && file.size < 128 * 1024) {
      setError(`File "${file.name}" is too small (${(file.size / 1024).toFixed(1)}KB). Minimum size is 128KB.`);
      return false;
    }
    return true;
  };

  // Upload old historical photos (multiple allowed, no restrictive clauses)
  const handleOldPhotosUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    setDuplicateDateWarning(''); // Clear previous warning

    for (const file of files) {
      if (!validateFileSize(file, true)) continue;

      try {
        setLoading(true);
        const response = await apiService.uploadOldPhoto(actualUserId, [file]);
        if (response.photos) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setOldPhotos(prev => [...prev, ...photosWithPreview]);

          const allValidations = response.all_validations || (response.validation_results ? [response.validation_results] : []);
          for (const vr of allValidations) {
            const reasons = vr.rejection_reasons || [];
            const dupReason = reasons.find(r => r.toLowerCase().includes('duplicate date'));
            if (dupReason) {
              setDuplicateDateWarning(dupReason);
              break;
            }
          }
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    e.target.value = '';
  };

  // Upload present photos
  const uploadPresentPhotos = useCallback(async (files) => {
    for (const file of files) {
      try {
        setLoading(true);
        const response = await apiService.uploadPresentPhotos(actualUserId, [file]);
        if (response.photos && response.photos.length > 0) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setPresentPhotos(prev => [...prev, ...photosWithPreview]);
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [actualUserId, loadUserPhotos]);

  // eslint-disable-next-line no-unused-vars
  const handlePresentFilesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (!validateFileSize(file, false)) {
        return;
      }
    }

    await uploadPresentPhotos(files);
    e.target.value = '';
  };

  const uploadPostAPhotos = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      try {
        setLoading(true);
        const response = await apiService.uploadPostAPhotos(actualUserId, [file]);
        if (response.photos && response.photos.length > 0) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setPostAPhotos(prev => [...prev, ...photosWithPreview]);
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [actualUserId, loadUserPhotos]);

  // eslint-disable-next-line no-unused-vars
  const handlePostAFilesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (!validateFileSize(file, false)) {
        return;
      }
    }

    await uploadPostAPhotos(files);
    e.target.value = '';
  };

  const uploadPostBPhotos = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      try {
        setLoading(true);
        const response = await apiService.uploadPostBPhotos(actualUserId, [file]);
        if (response.photos && response.photos.length > 0) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setPostBPhotos(prev => [...prev, ...photosWithPreview]);
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [actualUserId, loadUserPhotos]);

  // eslint-disable-next-line no-unused-vars
  const handlePostBFilesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (!validateFileSize(file, false)) {
        return;
      }
    }

    await uploadPostBPhotos(files);
    e.target.value = '';
  };

  // Delete a specific photo
  const handleDeletePhoto = async (photoId, type) => {
    if (!window.confirm('Are you sure you want to remove this image?')) return;
    try {
      setLoading(true);
      await apiService.deletePhoto(photoId);
      if (type === 'old') {
        setOldPhotos(prev => prev.filter(p => p.id !== photoId));
      } else if (type === 'present') {
        setPresentPhotos(prev => prev.filter(p => p.id !== photoId));
      } else if (type === 'post_a') {
        setPostAPhotos(prev => prev.filter(p => p.id !== photoId));
      } else if (type === 'post_b') {
        setPostBPhotos(prev => prev.filter(p => p.id !== photoId));
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete photo. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Remove all photos for a category
  const handleRemoveAllPhotos = async (type) => {
    const labels = { old: 'Historical', present: 'Present Neutral', post_a: 'Variation A', post_b: 'Variation B' };
    const label = labels[type] || type;
    if (!window.confirm(`Are you sure you want to delete ALL ${label} photos? This cannot be undone.`)) return;

    try {
      setLoading(true);
      const toDelete = type === 'old' ? [...oldPhotos]
        : type === 'present' ? [...presentPhotos]
        : type === 'post_a' ? [...postAPhotos]
        : [...postBPhotos];

      await Promise.all(toDelete.map(p => apiService.deletePhoto(p.id).catch(e => console.warn(`Failed to delete photo ${p.id}:`, e))));

      if (type === 'old') setOldPhotos([]);
      else if (type === 'present') setPresentPhotos([]);
      else if (type === 'post_a') setPostAPhotos([]);
      else if (type === 'post_b') setPostBPhotos([]);

    } catch (err) {
      console.error('Error deleting all photos:', err);
      setError('Failed to remove all photos. Some may have been left behind.');
    } finally {
      setLoading(false);
    }
  };

  // Helper to match photos to pose tasks accurately
  const getPosePhoto = useCallback((photos, poseKey, poseIndex) => {
    if (!photos || photos.length === 0) return null;
    const found = photos.find(p => {
      const val = p.validation_results || {};
      const detected = (val.detected_pose || '').toLowerCase();
      if (poseKey === 'up' && detected === 'up') return true;
      if (poseKey === 'down' && detected === 'down') return true;
      if (poseKey === 'left' && detected === 'left') return true;
      if (poseKey === 'right' && detected === 'right') return true;
      if (poseKey === 'up_left' && (detected === 'up-left' || detected === 'up_left')) return true;
      if (poseKey === 'up_right' && (detected === 'up-right' || detected === 'up_right')) return true;
      if (poseKey === 'down_left' && (detected === 'down-left' || detected === 'down_left')) return true;
      if (poseKey === 'down_right' && (detected === 'down-right' || detected === 'down_right')) return true;
      return false;
    });
    return found || photos[poseIndex] || null;
  }, []);

  const getTaskExampleUrl = useCallback((categoryOrTag) => {
    if (!categoryOrTag || !examplePhotos || examplePhotos.length === 0) return null;
    const key = categoryOrTag.toLowerCase();
    const match = examplePhotos.find(p => {
      const cat = (p.category || '').toLowerCase();
      return cat.includes(key) || key.includes(cat);
    });
    if (match) {
      return `${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${match.id}/file`;
    }
    return null;
  }, [examplePhotos]);

  const handleTaskCapture = useCallback((task) => {
    setActiveTaskForCamera(task);
    setShowCamera(true);
  }, []);

  const handleTaskUpload = useCallback(async (file, task) => {
    if (!file) return;
    if (task.stage === 'Present_Neutral') {
      await uploadPresentPhotos([file]);
      setSelfieCooldownSeconds(60);
      setActiveSelfieIndex(prev => Math.min(4, prev + 1));
    } else if (task.stage === 'Pose_Variation_A') {
      await uploadPostAPhotos([file]);
      setActivePoseAIndex(prev => Math.min(3, prev + 1));
    } else if (task.stage === 'Pose_Variation_B') {
      await uploadPostBPhotos([file]);
      setActivePoseBIndex(prev => Math.min(3, prev + 1));
    }
  }, [uploadPresentPhotos, uploadPostAPhotos, uploadPostBPhotos]);

  const handleTaskRetake = useCallback(async (photo, task) => {
    if (photo && photo.id) {
      const type = task.stage === 'Present_Neutral' ? 'present' : (task.stage === 'Pose_Variation_A' ? 'post_a' : 'post_b');
      await handleDeletePhoto(photo.id, type);
    }
  }, []);

  const validOldPhotos = oldPhotos.filter(p => p.is_valid || p.validation_results?.is_valid);
  const validPresentPhotos = presentPhotos.filter(p => p.is_valid || p.validation_results?.is_valid);
  
  // User ID assignment rule: EVEN User ID -> Pose Variation A; ODD User ID -> Pose Variation B
  const isAssignedVariationA = actualUserId ? (Number(actualUserId) % 2 === 0) : true;
  const isAssignedVariationB = !isAssignedVariationA;

  // Progress calculations: 5 selfies + 4 assigned poses = 9 items total
  const completedSelfies = Math.min(5, validPresentPhotos.length);
  const completedPoseA = Math.min(4, postAPhotos.length);
  const completedPoseB = Math.min(4, postBPhotos.length);
  const completedAssignedPoses = isAssignedVariationA ? completedPoseA : completedPoseB;
  const globalCompletedCount = completedSelfies + completedAssignedPoses;
  const globalProgressPercent = Math.min(100, Math.round((globalCompletedCount / 9) * 100));

  const isSelfiesSatisfied = validPresentPhotos.length >= 2;
  const isPosesSatisfied = isAssignedVariationA ? (postAPhotos.length >= 2) : (postBPhotos.length >= 2);
  const isHistorySatisfied = validOldPhotos.length >= 3;
  const isReadyForHandIn = isSelfiesSatisfied && isPosesSatisfied && isHistorySatisfied;

  // Sequence progression: Historical (Step 1) -> Selfies (Step 2) -> Poses (Step 3) -> Review (Step 4)
  // eslint-disable-next-line no-unused-vars
  const canAccessSelfies = oldPhotos.length >= 1;
  // eslint-disable-next-line no-unused-vars
  const canAccessPoses = presentPhotos.length >= 1;
  // eslint-disable-next-line no-unused-vars
  const canAccessHandIn = isReadyForHandIn;

  const goToDashboard = () => {
    navigate(`/dashboard/${encodeURIComponent(userName)}`);
  };

  return (
    <div className="photo-upload-container">
      {/* Mobile-First Header & Decluttered Info */}
      <div className="mobile-view-header">
        <div className="mobile-header-text">
          <h2 className="mobile-step-title">
            Imaginary <span className="title-user-name">— {displayName}</span>
          </h2>
          <p className="mobile-step-subtitle">
            {currentStep === 'old' && 'Step 1: Upload past photos from your gallery (No timing clauses).'}
            {currentStep === 'present' && 'Step 2: Capture 5 live selfies with different backgrounds.'}
            {(currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') &&
              `Step 3: Capture head poses — ${isAssignedVariationA ? 'Variation A' : 'Variation B'}. Hold still when green to auto-snap.`}
            {currentStep === 'hand_in' && 'Step 4: Final verification review & submission.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowWhyModal(true)}
          className="why-photos-link-btn"
          title="Learn why these photos are needed"
        >
          <span>Why do we need these photos?</span>
          <span className="why-info-badge">ⓘ</span>
        </button>
      </div>

      {/* Global Progress Bar: Completed / 9 * 100 */}
      <div className="global-progress-banner">
        <div className="global-progress-header">
          <div>
            <span className="global-progress-title">Verification Progress</span>
            <span className="global-progress-sub">
              Target: 5 Selfies + 4 Head Poses
            </span>
          </div>
          <span className={`global-progress-badge ${globalProgressPercent === 100 ? 'done' : ''}`}>
            {globalCompletedCount}/9 Tasks ({globalProgressPercent}%)
          </span>
        </div>
        <div className="global-progress-track">
          <div
            className="global-progress-fill"
            style={{ width: `${globalProgressPercent}%` }}
          />
        </div>
      </div>

      {/* Horizontal Stepper Progress Bar: 1. Historical -> 2. Selfies -> 3. Poses -> 4. Review */}
      <div className="horizontal-stepper-bar">
        {/* Node 1: Historical (Step 1) */}
        <button
          type="button"
          className={`stepper-node-btn ${currentStep === 'old' ? 'active' : ''} ${isHistorySatisfied ? 'completed' : ''}`}
          onClick={() => setCurrentStep('old')}
        >
          <div className="stepper-node-circle">
            {isHistorySatisfied ? '✓' : '1'}
          </div>
          <div className="stepper-node-label">
            <span className="stepper-node-name">Historical</span>
            <span className="stepper-node-count">{validOldPhotos.length}/3+</span>
          </div>
        </button>

        <div className={`stepper-divider ${isHistorySatisfied ? 'completed' : ''}`}></div>

        {/* Node 2: Selfies (Step 2) */}
        <button
          type="button"
          className={`stepper-node-btn ${currentStep === 'present' ? 'active' : ''} ${isSelfiesSatisfied ? 'completed' : ''}`}
          onClick={() => setCurrentStep('present')}
        >
          <div className="stepper-node-circle">
            {isSelfiesSatisfied ? '✓' : '2'}
          </div>
          <div className="stepper-node-label">
            <span className="stepper-node-name">Selfies</span>
            <span className="stepper-node-count">{validPresentPhotos.length}/5</span>
          </div>
        </button>

        <div className={`stepper-divider ${isSelfiesSatisfied ? 'completed' : ''}`}></div>

        {/* Node 3: Poses (Step 3) */}
        <button
          type="button"
          className={`stepper-node-btn ${(currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') ? 'active' : ''} ${isPosesSatisfied ? 'completed' : ''}`}
          onClick={() => setCurrentStep('poses')}
        >
          <div className="stepper-node-circle">
            {isPosesSatisfied ? '✓' : '3'}
          </div>
          <div className="stepper-node-label">
            <span className="stepper-node-name">Poses ({isAssignedVariationA ? 'Var A' : 'Var B'})</span>
            <span className="stepper-node-count">{completedAssignedPoses}/4</span>
          </div>
        </button>

        <div className={`stepper-divider ${isPosesSatisfied ? 'completed' : ''}`}></div>

        {/* Node 4: Review / Hand In */}
        <button
          type="button"
          className={`stepper-node-btn ${currentStep === 'hand_in' ? 'active' : ''} ${isReadyForHandIn ? 'completed' : ''}`}
          onClick={() => setCurrentStep('hand_in')}
        >
          <div className="stepper-node-circle">
            {isReadyForHandIn ? '✓' : '4'}
          </div>
          <div className="stepper-node-label">
            <span className="stepper-node-name">Review</span>
            <span className="stepper-node-count">{isReadyForHandIn ? 'Ready' : 'In Progress'}</span>
          </div>
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* STEP 2: PRESENT SELFIES (GUIDED 5-SHOT SEQUENCE) */}
      {currentStep === 'present' && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 2: Live Selfie Captures</h3>
            <p>Follow each card one-by-one. Each shot guides you through exact expression, glasses, and background rules.</p>
          </div>

          {/* Verified Selfies Progress Bar */}
          <div className="photo-progress">
            <div className="progress-header">
              <span>Verified Selfies: <strong>{validPresentPhotos.length} / 5</strong></span>
              <span>Target: 5 Selfies (Min 2 required to unlock Poses)</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${Math.min((validPresentPhotos.length / 5) * 100, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Stepper Tabs Bar for Selfies 1-5 */}
          <div className="task-stepper-bar">
            {SELFIE_TASKS.map((st, idx) => {
              const isCompleted = idx < validPresentPhotos.length;
              const isActive = idx === activeSelfieIndex;
              return (
                <button
                  key={st.id}
                  type="button"
                  className={`stepper-pill ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                  onClick={() => setActiveSelfieIndex(idx)}
                >
                  <span className="stepper-num">Selfie {idx + 1}</span>
                  <span className="stepper-status-dot">
                    {isCompleted ? '✅' : (isActive ? '👉' : '🔒')}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Dedicated Step-by-Step Guided PhotoTaskCard */}
          <PhotoTaskCard
            task={SELFIE_TASKS[activeSelfieIndex]}
            photo={validPresentPhotos[activeSelfieIndex]}
            status={
              validPresentPhotos[activeSelfieIndex]
                ? 'ACCEPTED'
                : (selfieCooldownSeconds > 0
                    ? 'COOLDOWN'
                    : (activeSelfieIndex > validPresentPhotos.length ? 'LOCKED' : 'READY'))
            }
            cooldownSeconds={selfieCooldownSeconds}
            examplePhotoUrl={getTaskExampleUrl(SELFIE_TASKS[activeSelfieIndex]?.exampleCategory)}
            onCapture={handleTaskCapture}
            onUpload={handleTaskUpload}
            onRetake={handleTaskRetake}
            onNext={() => setActiveSelfieIndex(prev => Math.min(4, prev + 1))}
            hasNext={activeSelfieIndex < 4}
            totalTasks={5}
            currentIndex={activeSelfieIndex}
          />

          {/* Toggle for Reviewing All Uploaded Selfies */}
          {presentPhotos.length > 0 && (
            <div style={{ margin: '0.75rem 0 1rem 0', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setShowAllPresentPhotos(!showAllPresentPhotos)}
                style={{
                  background: 'transparent',
                  border: '1px solid #cbd5e1',
                  padding: '0.45rem 1rem',
                  borderRadius: '8px',
                  color: '#475569',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                {showAllPresentPhotos ? '▲ Hide All Uploaded Selfies' : `▼ Review All Uploaded Selfies (${presentPhotos.length})`}
              </button>
            </div>
          )}

          {/* Present Photos Inspection Grid */}
          {showAllPresentPhotos && presentPhotos.length > 0 && (
            <div className="photo-inspection-section">
              <div className="inspection-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4>Uploaded Present Photos ({presentPhotos.length})</h4>
                <button
                  type="button"
                  onClick={() => handleRemoveAllPhotos('present')}
                  style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.4rem 0.8rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}
                >
                  Remove All
                </button>
              </div>

              <div className="cards-grid">
                {presentPhotos.map((photo, index) => {
                  const val = photo.validation_results || {};
                  const isValid = photo.is_valid || val.is_valid;
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);

                  return (
                    <div key={photo.id || index} className={`photo-card ${isValid ? 'card-accepted' : 'card-rejected'}`}>
                      <div className="photo-preview-box">
                        <img
                          src={photoUrl}
                          alt={`Selfie upload ${index + 1}`}
                          className="preview-img"
                          onClick={() => setSelectedImage(photoUrl)}
                        />
                        <div className={`status-pill ${isValid ? 'pill-success' : 'pill-danger'}`}>
                          {isValid ? '✅ Accepted' : '❌ Rejected'}
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="card-title-row">
                          <h5>Selfie #{index + 1}</h5>
                          <button
                            type="button"
                            onClick={() => handleDeletePhoto(photo.id, 'present')}
                            className="delete-photo-btn"
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </div>
                        <div className="checks-list">
                          <div className="check-row">
                            <span>Eyewear:</span>
                            <span style={{ fontWeight: 600, color: val.has_glasses ? '#d97706' : '#16a34a' }}>
                              {val.has_glasses ? '👓 Glasses' : '👁️ No glasses'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Front Facing:</span>
                            <span className={val.front_facing !== false ? 'text-ok' : 'text-bad'}>
                              {val.front_facing !== false ? '✓ Direct' : '✗ Angled'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP 3: POSES (DIRECTIONAL & DIAGONAL GUIDED SEQUENCE) */}
      {(currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 3: Head Poses — {isAssignedVariationA ? 'Variation A (Cardinal Angles)' : 'Variation B (Diagonal Angles)'}</h3>
            <p>
              You are assigned <strong>{isAssignedVariationA ? 'Pose Variation A (Up, Down, Left, Right)' : 'Pose Variation B (Diagonal Angles)'}</strong>.
              Follow the real-time directional indicator. When your head angle matches, the oval turns green — hold still and the camera snaps automatically!
            </p>
          </div>

          {/* Render ONLY the assigned variation view! The other variation is NOT shown */}
          {isAssignedVariationA ? (
            <div>
              {/* Stepper Tabs for Variation A */}
              <div className="task-stepper-bar">
                {POSE_A_TASKS.map((pt, idx) => {
                  const assignedPhoto = getPosePhoto(postAPhotos, pt.arrow === '⬆️' ? 'up' : (pt.arrow === '⬇️' ? 'down' : (pt.arrow === '⬅️' ? 'left' : 'right')), idx);
                  const isCompleted = assignedPhoto && (assignedPhoto.is_valid || assignedPhoto.validation_results?.is_valid);
                  const isActive = idx === activePoseAIndex;
                  return (
                    <button
                      key={pt.id}
                      type="button"
                      className={`stepper-pill ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                      onClick={() => setActivePoseAIndex(idx)}
                    >
                      <span className="stepper-num">{pt.arrow} Pose {idx + 1}</span>
                      <span className="stepper-status-dot">
                        {isCompleted ? '✅' : (isActive ? '👉' : '🔒')}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Dedicated Step-by-Step Guided PhotoTaskCard for Pose A */}
              {(() => {
                const pt = POSE_A_TASKS[activePoseAIndex];
                const assignedPhoto = getPosePhoto(postAPhotos, pt.arrow === '⬆️' ? 'up' : (pt.arrow === '⬇️' ? 'down' : (pt.arrow === '⬅️' ? 'left' : 'right')), activePoseAIndex);
                return (
                  <PhotoTaskCard
                    task={pt}
                    photo={assignedPhoto}
                    status={assignedPhoto && (assignedPhoto.is_valid || assignedPhoto.validation_results?.is_valid) ? 'ACCEPTED' : 'READY'}
                    cooldownSeconds={0}
                    examplePhotoUrl={getTaskExampleUrl(pt.exampleCategory)}
                    onCapture={handleTaskCapture}
                    onUpload={handleTaskUpload}
                    onRetake={handleTaskRetake}
                    onNext={() => setActivePoseAIndex(prev => Math.min(3, prev + 1))}
                    hasNext={activePoseAIndex < 3}
                    totalTasks={4}
                    currentIndex={activePoseAIndex}
                  />
                );
              })()}

              {/* Toggle for Reviewing All Variation A Photos */}
              {postAPhotos.length > 0 && (
                <div style={{ margin: '0.75rem 0 1rem 0', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setShowAllPostAPhotos(!showAllPostAPhotos)}
                    style={{
                      background: 'transparent',
                      border: '1px solid #cbd5e1',
                      padding: '0.45rem 1rem',
                      borderRadius: '8px',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    {showAllPostAPhotos ? '▲ Hide All Variation A Photos' : `▼ Review All Variation A Photos (${postAPhotos.length})`}
                  </button>
                </div>
              )}

              {showAllPostAPhotos && (
                <div className="photo-inspection-section">
                  <div className="cards-grid">
                    {postAPhotos.map((photo, index) => {
                      const val = photo.validation_results || {};
                      const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);
                      return (
                        <div key={photo.id || index} className="photo-card card-accepted">
                          <div className="photo-preview-box">
                            <img src={photoUrl} alt={`Pose A ${index + 1}`} className="preview-img" onClick={() => setSelectedImage(photoUrl)} />
                          </div>
                          <div className="card-body">
                            <div className="card-title-row">
                              <h5>Pose #{index + 1}</h5>
                              <button type="button" onClick={() => handleDeletePhoto(photo.id, 'post_a')} className="delete-photo-btn"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg></button>
                            </div>
                            <div className="checks-list">
                              <div className="check-row">
                                <span>Detected:</span>
                                <strong>{val.detected_pose || 'UNASSIGNED'}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              {/* Stepper Tabs for Variation B */}
              <div className="task-stepper-bar">
                {POSE_B_TASKS.map((pt, idx) => {
                  const assignedPhoto = getPosePhoto(postBPhotos, pt.arrow === '↖️' ? 'up_left' : (pt.arrow === '↗️' ? 'up_right' : (pt.arrow === '↙️' ? 'down_left' : 'down_right')), idx);
                  const isCompleted = assignedPhoto && (assignedPhoto.is_valid || assignedPhoto.validation_results?.is_valid);
                  const isActive = idx === activePoseBIndex;
                  return (
                    <button
                      key={pt.id}
                      type="button"
                      className={`stepper-pill ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                      onClick={() => setActivePoseBIndex(idx)}
                    >
                      <span className="stepper-num">{pt.arrow} Pose {idx + 5}</span>
                      <span className="stepper-status-dot">
                        {isCompleted ? '✅' : (isActive ? '👉' : '🔒')}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Dedicated Step-by-Step Guided PhotoTaskCard for Pose B */}
              {(() => {
                const pt = POSE_B_TASKS[activePoseBIndex];
                const assignedPhoto = getPosePhoto(postBPhotos, pt.arrow === '↖️' ? 'up_left' : (pt.arrow === '↗️' ? 'up_right' : (pt.arrow === '↙️' ? 'down_left' : 'down_right')), activePoseBIndex);
                return (
                  <PhotoTaskCard
                    task={pt}
                    photo={assignedPhoto}
                    status={assignedPhoto && (assignedPhoto.is_valid || assignedPhoto.validation_results?.is_valid) ? 'ACCEPTED' : 'READY'}
                    cooldownSeconds={0}
                    examplePhotoUrl={getTaskExampleUrl(pt.exampleCategory)}
                    onCapture={handleTaskCapture}
                    onUpload={handleTaskUpload}
                    onRetake={handleTaskRetake}
                    onNext={() => setActivePoseBIndex(prev => Math.min(3, prev + 1))}
                    hasNext={activePoseBIndex < 3}
                    totalTasks={4}
                    currentIndex={activePoseBIndex}
                  />
                );
              })()}

              {/* Toggle for Reviewing All Variation B Photos */}
              {postBPhotos.length > 0 && (
                <div style={{ margin: '0.75rem 0 1rem 0', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setShowAllPostBPhotos(!showAllPostBPhotos)}
                    style={{
                      background: 'transparent',
                      border: '1px solid #cbd5e1',
                      padding: '0.45rem 1rem',
                      borderRadius: '8px',
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      cursor: 'pointer'
                    }}
                  >
                    {showAllPostBPhotos ? '▲ Hide All Variation B Photos' : `▼ Review All Variation B Photos (${postBPhotos.length})`}
                  </button>
                </div>
              )}

              {showAllPostBPhotos && (
                <div className="photo-inspection-section">
                  <div className="cards-grid">
                    {postBPhotos.map((photo, index) => {
                      const val = photo.validation_results || {};
                      const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);
                      return (
                        <div key={photo.id || index} className="photo-card card-accepted">
                          <div className="photo-preview-box">
                            <img src={photoUrl} alt={`Pose B ${index + 1}`} className="preview-img" onClick={() => setSelectedImage(photoUrl)} />
                          </div>
                          <div className="card-body">
                            <div className="card-title-row">
                              <h5>Pose #{index + 5}</h5>
                              <button type="button" onClick={() => handleDeletePhoto(photo.id, 'post_b')} className="delete-photo-btn"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg></button>
                            </div>
                            <div className="checks-list">
                              <div className="check-row">
                                <span>Detected:</span>
                                <strong>{val.detected_pose || 'UNASSIGNED'}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* STEP 1: HISTORICAL PHOTOS (FIRST STEP - NO RESTRICTIVE CLAUSES) */}
      {currentStep === 'old' && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 1: Historical Photos</h3>
            <p>Upload at least 3 genuine historical photos from your device gallery. There are no restrictive clauses or timing limits—simply select past photos from your life stages.</p>
          </div>

          {/* Simple Gallery Upload Box - No Clauses */}
          <div className="file-upload">
            <label
              className={`file-upload-label mobile-upload-dropzone ${isDragging ? 'drag-active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, handleOldPhotosUpload)}
            >
              <div className="upload-camera-badge" style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <span className="upload-primary-text">Select historical photos from your gallery</span>
              <span className="upload-subtext">Original camera photos & scans • Min 3 photos required • No time limits</span>
              <input
                type="file"
                multiple
                accept="image/*,.heic,.heif"
                onChange={handleOldPhotosUpload}
                disabled={loading}
                className="file-input"
              />
            </label>
          </div>

          {duplicateDateWarning && (
            <div className="warning-banner" style={{ marginTop: '1rem', padding: '0.85rem 1rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
              <span>{duplicateDateWarning}</span>
            </div>
          )}

          {/* Historical Photos Inspection Grid */}
          {oldPhotos.length > 0 && (
            <div className="photo-inspection-section" style={{ marginTop: '2rem' }}>
              <div className="inspection-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4>Uploaded Historical Photos ({oldPhotos.length})</h4>
                <button
                  type="button"
                  onClick={() => handleRemoveAllPhotos('old')}
                  style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.4rem 0.8rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}
                >
                  Remove All
                </button>
              </div>

              <div className="cards-grid">
                {oldPhotos.map((photo, index) => {
                  const val = photo.validation_results || {};
                  const isValid = photo.is_valid || val.is_valid;
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);

                  return (
                    <div key={photo.id || index} className={`photo-card ${isValid ? 'card-accepted' : 'card-rejected'}`}>
                      <div className="photo-preview-box">
                        <img
                          src={photoUrl}
                          alt={`Historical #${index + 1}`}
                          className="preview-img"
                          onClick={() => setSelectedImage(photoUrl)}
                        />
                        <div className={`status-pill ${isValid ? 'pill-success' : 'pill-danger'}`}>
                          {isValid ? '✅ Accepted' : '❌ Rejected'}
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="card-title-row">
                          <h5>Photo #{index + 1}</h5>
                          <button
                            type="button"
                            onClick={() => handleDeletePhoto(photo.id, 'old')}
                            className="delete-photo-btn"
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                          </button>
                        </div>
                        <div className="checks-list">
                          <div className="check-row">
                            <span>EXIF Date:</span>
                            <span className={val.exif_date ? 'text-ok' : 'text-bad'}>
                              {val.exif_date ? val.exif_date.split(' ')[0] : 'None'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP 4: REVIEW & HAND IN */}
      {currentStep === 'hand_in' && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 4: Verification Review & Submission</h3>
            <p>Review your submitted photos and complete package verification.</p>
          </div>

          <div className="summary-status-card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '1.25rem', marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#0f172a' }}>Submission Readiness</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>1. Historical Photos (Min 3):</span>
                <strong style={{ color: isHistorySatisfied ? '#16a34a' : '#ef4444' }}>
                  {isHistorySatisfied ? '✓ Ready' : `${validOldPhotos.length}/3`}
                </strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>2. Present Selfies (Target 5, Min 2):</span>
                <strong style={{ color: isSelfiesSatisfied ? '#16a34a' : '#ef4444' }}>
                  {isSelfiesSatisfied ? '✓ Ready' : `${validPresentPhotos.length}/2`}
                </strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>3. Head Poses (Target 4, Min 2):</span>
                <strong style={{ color: isPosesSatisfied ? '#16a34a' : '#ef4444' }}>
                  {isPosesSatisfied ? '✓ Ready' : `${completedAssignedPoses}/2`}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Bottom Navigation Footer */}
      <div className="sticky-mobile-footer">
        <div className="sticky-footer-inner">
          <div className="sticky-helper-text">
            {currentStep === 'old' && (
              isHistorySatisfied ? (
                <span className="hint-success">✅ {validOldPhotos.length} historical photos accepted</span>
              ) : (
                <span className="hint-pending">📸 Step 1: Upload past photos from your gallery ({validOldPhotos.length}/3 required)</span>
              )
            )}
            {currentStep === 'present' && (
              validPresentPhotos.length >= 2 ? (
                <span className="hint-success">✅ {validPresentPhotos.length} selfies verified ({validPresentPhotos.length}/5)</span>
              ) : presentPhotos.length >= 1 ? (
                <span className="hint-pending">🤳 {presentPhotos.length} selfie(s) captured — continue or capture more</span>
              ) : (
                <span className="hint-pending">🤳 Step 2: Capture live front selfies (target 5)</span>
              )
            )}
            {(currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') && (
              isPosesSatisfied ? (
                <span className="hint-success">✅ {completedAssignedPoses} poses verified</span>
              ) : (
                <span className="hint-pending">📐 Step 3: Complete required head poses ({completedAssignedPoses}/2 min required)</span>
              )
            )}
            {currentStep === 'hand_in' && (
              isReadyForHandIn ? (
                <span className="hint-success">🎉 All verification requirements satisfied!</span>
              ) : (
                <span className="hint-warning">⚠️ Complete remaining required items above</span>
              )
            )}
          </div>

          <div className="sticky-dual-btn-group">
            {/* Back Button */}
            {currentStep !== 'old' && (
              <button
                type="button"
                className="sticky-action-btn secondary-btn"
                onClick={() => {
                  if (currentStep === 'present') setCurrentStep('old');
                  else if (currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') setCurrentStep('present');
                  else if (currentStep === 'hand_in') setCurrentStep('poses');
                }}
              >
                ← Back
              </button>
            )}

            {/* Step 1: Historical forward button */}
            {currentStep === 'old' && (
              <button
                type="button"
                className="sticky-action-btn primary-btn"
                onClick={() => setCurrentStep('present')}
              >
                <span>Continue to Live Selfies</span>
                <span className="btn-chevron">→</span>
              </button>
            )}

            {/* Step 2: Selfies forward button (Never stuck! Accessible when selfie is taken or clicked) */}
            {currentStep === 'present' && (
              <button
                type="button"
                className="sticky-action-btn primary-btn"
                onClick={() => setCurrentStep('poses')}
              >
                <span>Continue to Head Poses</span>
                <span className="btn-chevron">→</span>
              </button>
            )}

            {/* Step 3: Poses forward button */}
            {(currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') && (
              <button
                type="button"
                className="sticky-action-btn primary-btn"
                onClick={() => setCurrentStep('hand_in')}
              >
                <span>Review Submission</span>
                <span className="btn-chevron">→</span>
              </button>
            )}

            {/* Step 4: Finalize button */}
            {currentStep === 'hand_in' && (
              <button
                type="button"
                className="sticky-action-btn primary-btn submit-btn"
                onClick={goToDashboard}
                disabled={!isReadyForHandIn}
              >
                <span>Finalize & Submit</span>
                <span className="btn-chevron">✓</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Unified High-Assurance Live Capture Modal */}
      <LiveCaptureModal
        isOpen={showCamera}
        onClose={() => {
          setShowCamera(false);
          setActiveTaskForCamera(null);
        }}
        onCapture={async (file) => {
          const task = activeTaskForCamera || (
            currentStep === 'present'
              ? SELFIE_TASKS[activeSelfieIndex]
              : (isAssignedVariationB ? POSE_B_TASKS[activePoseBIndex] : POSE_A_TASKS[activePoseAIndex])
          );
          if (task.stage === 'Present_Neutral') {
            await uploadPresentPhotos([file]);
            setSelfieCooldownSeconds(60);
            setActiveSelfieIndex(prev => Math.min(4, prev + 1));
          } else if (task.stage === 'Pose_Variation_A') {
            await uploadPostAPhotos([file]);
            setActivePoseAIndex(prev => Math.min(3, prev + 1));
          } else if (task.stage === 'Pose_Variation_B') {
            await uploadPostBPhotos([file]);
            setActivePoseBIndex(prev => Math.min(3, prev + 1));
          }
          setShowCamera(false);
          setActiveTaskForCamera(null);
        }}
        stage={activeTaskForCamera ? activeTaskForCamera.stage : (currentStep === 'present' ? 'Present_Neutral' : (isAssignedVariationB ? 'Pose_Variation_B' : 'Pose_Variation_A'))}
        poseNumber={activeTaskForCamera ? activeTaskForCamera.number : (currentStep === 'present' ? (activeSelfieIndex + 1) : (isAssignedVariationB ? (activePoseBIndex + 5) : (activePoseAIndex + 1)))}
        currentCount={
          (activeTaskForCamera?.stage === 'Present_Neutral' || currentStep === 'present') ? validPresentPhotos.length :
          (isAssignedVariationB ? postBPhotos.length : postAPhotos.length)
        }
        maxCount={(activeTaskForCamera?.stage === 'Present_Neutral' || currentStep === 'present') ? 5 : 4}
        title={activeTaskForCamera ? activeTaskForCamera.title : (currentStep === 'present' ? "Present Selfie Verification" : "Pose Verification")}
        description={activeTaskForCamera ? (activeTaskForCamera.caption || activeTaskForCamera.clause) : "Align your face in the oval to capture"}
      />

      {/* Why We Need Photos Modal */}
      {showWhyModal && (
        <div className="lightbox-overlay" onClick={() => setShowWhyModal(false)}>
          <div className="lightbox-content why-modal-card" onClick={e => e.stopPropagation()}>
            <button type="button" className="lightbox-close" onClick={() => setShowWhyModal(false)}>&times;</button>
            <div className="why-modal-body">
              <span className="why-modal-badge">✨ AI Age Progression Project</span>
              <h3 className="why-modal-title">Why do we need these photos?</h3>
              <p className="why-modal-lead">
                Your photos are strictly used to evaluate and train authentic, high-fidelity facial aging and pose progression algorithms.
              </p>
              <ul className="why-modal-list">
                <li>
                  <strong>Historical Photos First (Step 1):</strong> Genuine photos from different life stages allow models to learn authentic human aging trajectories with zero upload delays.
                </li>
                <li>
                  <strong>Live Selfies (Step 2):</strong> 5 front-facing selfies with background variety guarantee identity liveness and multi-lighting calibration.
                </li>
                <li>
                  <strong>Directional & Diagonal Poses (Step 3):</strong> 8 head poses train 3D facial mesh rendering across natural head rotation angles.
                </li>
              </ul>
              <div style={{ marginTop: '1.25rem', textAlign: 'right' }}>
                <button
                  type="button"
                  className="sticky-action-btn primary-btn"
                  style={{ display: 'inline-block', width: 'auto', padding: '0.6rem 1.5rem' }}
                  onClick={() => setShowWhyModal(false)}
                >
                  Understood
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Overlay for inspecting images */}
      {selectedImage && (
        <div className="lightbox-overlay" onClick={() => setSelectedImage(null)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button type="button" className="lightbox-close" onClick={() => setSelectedImage(null)}>&times;</button>
            <img src={selectedImage} alt="Expanded view" />
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
